import { actionPermissions } from "./actionPermissions";
import { PermissionList } from "./PermissionList";

const LegacyPerm = <TServiceId extends string, TActionId extends string>(
  serviceId: TServiceId,
  actionId: TActionId,
): `${TServiceId}.${TActionId}` => `${serviceId}.${actionId}` as any;

const legacyCrudPerms = <TServiceId extends string>(serviceId: TServiceId) =>
  PermissionList([
    LegacyPerm(serviceId, "detail"),
    LegacyPerm(serviceId, "list"),
    LegacyPerm(serviceId, "edit"),
  ]);

const LegacyDirectoryPerm = <TAction extends string, TDirectory extends string>(
  action: TAction,
  directory: TDirectory,
): `${TAction}#${TDirectory}` => `${action}#${directory}` as any;

const legacyDecisionPerms = <TDirectory extends string>(dir: TDirectory) =>
  PermissionList([
    LegacyDirectoryPerm("management.decision.list", dir),
    LegacyDirectoryPerm("management.decision.detail", dir),
    LegacyDirectoryPerm("management.decision.resolve", dir),
  ]);

const legacyRegionalPerms = <TAction extends string>(action: TAction) =>
  PermissionList([
    LegacyDirectoryPerm(action, "MarkiObce"),
    LegacyDirectoryPerm(action, "BielskoBiała"),
    LegacyDirectoryPerm(action, "Częstochowa"),
    LegacyDirectoryPerm(action, "Kielce"),
    LegacyDirectoryPerm(action, "Kraków"),
    LegacyDirectoryPerm(action, "Przemyśl"),
    LegacyDirectoryPerm(action, "Bydgoszcz"),
    LegacyDirectoryPerm(action, "Gdańsk"),
    LegacyDirectoryPerm(action, "Koszalin"),
    LegacyDirectoryPerm(action, "Olsztyn"),
    LegacyDirectoryPerm(action, "Szczecin"),
    LegacyDirectoryPerm(action, "Białystok"),
    LegacyDirectoryPerm(action, "Lublin"),
    LegacyDirectoryPerm(action, "Łódź"),
    LegacyDirectoryPerm(action, "Radom"),
    LegacyDirectoryPerm(action, "Warszawa"),
    LegacyDirectoryPerm(action, "JeleniaGóra"),
    LegacyDirectoryPerm(action, "Kalisz"),
    LegacyDirectoryPerm(action, "Poznań"),
    LegacyDirectoryPerm(action, "Wrocław"),
    LegacyDirectoryPerm(action, "ZielonaGóra"),
    LegacyDirectoryPerm(action, "EXPORT"),
  ]);

const legacyCrmPermissions = PermissionList([
  ...legacyCrudPerms("crm.salesperson"),
  ...legacyCrudPerms("crm.route"),
]);

const legacyPlmPermissions = PermissionList([
  // PLM
  ...legacyCrudPerms("plm.brand"),
  ...legacyCrudPerms("plm.komponent"),
  ...legacyCrudPerms("plm.productionRecipe"),
  ...legacyCrudPerms("plm.rodzajKomponentu"),
  ...legacyCrudPerms("plm.ingredientGroup"),
  ...legacyCrudPerms("plm.receptura"),
  ...legacyCrudPerms("plm.zastosowanieCategory"),
  ...legacyCrudPerms("plm.produkt"),
  ...legacyCrudPerms("plm.productGroup"),
  ...legacyCrudPerms("plm.czescBudynku"),
  ...legacyCrudPerms("plm.obiektZastosowania"),
  ...legacyCrudPerms("plm.narzedzie"),
  ...legacyCrudPerms("plm.rozpuszczalnik"),
  ...legacyCrudPerms("plm.rodzajOpakowania"),
  ...legacyCrudPerms("plm.podloze"),
  ...legacyCrudPerms("plm.productList"),
  ...legacyCrudPerms("plm.jednostkaSprzedazy"),
  ...legacyCrudPerms("plm.wyrob"),
  ...legacyCrudPerms("plm.language"),
  ...legacyCrudPerms("plm.opakowanie"),
  ...legacyCrudPerms("plm.asortyment"),
  "plm.komponent.dump",
  "plm.receptura.dump",
  "plm.produkt.dump",
  "plm.wyrob.dump",
  "plm.asortyment.dump",
  "plm.productionRecipe.ingredientsTable",
]);

export const permissions = PermissionList([
  "management.decision.list",
  "management.decision.detail",
  "management.decision.resolve",
  "maintenance.maintenance.rebuildIndices",
  "maintenance.maintenance.pendingMigrations",
  "maintenance.maintenance.runMigrations",
  "search.search.search",

  "invoices.invoiceThreads.retrieveAnyThread",
  "invoices.invoiceThreads.retrieveMyThread",
  "invoices.invoiceThreads.moderate",

  ...actionPermissions,
  ...legacyCrmPermissions,
  ...legacyPlmPermissions,
  ...legacyDecisionPerms("plm.productList"),
  ...legacyDecisionPerms("crm.route"),
  ...legacyDecisionPerms("plm.productionRecipe"),
  ...legacyDecisionPerms("plm.produkt"),
  ...legacyDecisionPerms("plm.receptura"),
  ...legacyDecisionPerms("plm.asortyment"),
  ...legacyDecisionPerms("plm.komponent"),
  ...legacyDecisionPerms("plm.wyrob"),

  ...legacyRegionalPerms("crm.route.list"),
]);

export type Permission = typeof permissions extends (infer T)[] ? T : never;
